import { useCallback, useEffect, useState } from 'react';
import { captureException } from '@sentry/nextjs';
import { useRouter } from 'next/router';
import { GetCoachSettingsQuery, useGetCoachSettingsLazyQuery } from 'types/generated/client';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import { useViewer } from 'hooks/useViewer';
import SafeAreaPage from 'layouts/SafeAreaPage';
import Footer from 'components/Footer';
import Head from 'components/utilities/Head';
import PopppinsFont from 'components/utilities/PoppinsFont';
import { CoachDashboard, OnboardingSteps } from './components';
import { CoachLoggedInScreenSkeleton } from './components/Skeletons';
import { OnboardingStepEnum } from './props';

const CoachLoggedInScreen = () => {
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState<OnboardingStepEnum>(OnboardingStepEnum.PROFILE);
  const [isOnboardingComplete, setIsOnboardingComplete] = useState<boolean | null>(null);
  const viewer = useViewer();
  const [getCoachSettings, { data: coachData, loading, called }] = useGetCoachSettingsLazyQuery();

  // Function to determine current step based on missing data
  const determineCurrentStep = useCallback(
    (data: GetCoachSettingsQuery['usersByPk']): OnboardingStepEnum | null => {
      if (!data) return OnboardingStepEnum.PROFILE;

      // Check Profile step requirements
      const profileMissing =
        !data.gender || !data.phoneNumber || !data.coachSports || data.coachSports.length === 0;

      if (profileMissing) {
        return OnboardingStepEnum.PROFILE;
      }

      // Check Schedule step requirements (lesson settings)
      const scheduleMissing = !data.priceUnitAmountCoachDefault;

      if (scheduleMissing) {
        return OnboardingStepEnum.SCHEDULE;
      }

      // Check Payments step requirements (bank account setup)
      // Skip Payments step if user has chosen to skip Stripe setup
      const paymentsMissing = !data.stripeMerchantChargesEnabled && !data.didSkipStripeSetup;

      if (paymentsMissing) {
        return OnboardingStepEnum.PAYMENTS;
      }

      // All steps complete - onboarding is done
      return null;
    },
    [],
  );

  const getCoachInfo = useCallback(async () => {
    try {
      await getCoachSettings({
        variables: {
          id: viewer.userId,
        },
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
      });
    } catch (err) {
      captureException(err);
    }
  }, [viewer.userId, getCoachSettings]);

  useEffect(() => {
    if (viewer.userId) {
      getCoachInfo();
    }
  }, [viewer.userId, getCoachInfo]);

  useEffect(() => {
    if (coachData?.usersByPk && !loading && router.isReady && isOnboardingComplete === null) {
      // Check if step query parameter is present and prioritize it
      const stepParam = router.query.step;
      let step: OnboardingStepEnum | null;

      if (stepParam && typeof stepParam === 'string') {
        // Check if the step parameter matches any of our enum values
        if (Object.values(OnboardingStepEnum).includes(stepParam as OnboardingStepEnum)) {
          step = stepParam as OnboardingStepEnum;
          // Clear the step query parameter from URL
          router.replace(router.asPath.split('?')[0], undefined, { shallow: true });
        } else {
          // Invalid step parameter, fall back to data-based determination
          step = determineCurrentStep(coachData.usersByPk);
        }
      } else {
        // No step parameter, use data-based determination
        step = determineCurrentStep(coachData.usersByPk);
      }

      setCurrentStep(step || OnboardingStepEnum.PROFILE);
      setIsOnboardingComplete(step === null);
    }
  }, [
    coachData,
    loading,
    determineCurrentStep,
    router.query.step,
    router.isReady,
    isOnboardingComplete,
  ]);

  const handleStepChange = (step: OnboardingStepEnum | null) => {
    setCurrentStep(step || OnboardingStepEnum.PROFILE);
    setIsOnboardingComplete(step === null);
  };

  return (
    <>
      <Head title="Home" description={'Coach Home'} canonicalUrl={process.env.APP_URL} />

      <PopppinsFont />
      <SafeAreaPage isShowTopNav isHideSidebar>
        <main className="pb-[6.5rem] max-sm:pb-0">
          {loading || !called || isOnboardingComplete === null ? (
            <CoachLoggedInScreenSkeleton />
          ) : isOnboardingComplete ? (
            <CoachDashboard />
          ) : (
            <OnboardingSteps
              currentStep={currentStep}
              setCurrentStep={handleStepChange}
              coachData={coachData?.usersByPk}
              isLoading={loading}
            />
          )}
        </main>
      </SafeAreaPage>
      <Footer />
    </>
  );
};

export default CoachLoggedInScreen;
